import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SHAPE_CORNERS } from "@/lib/shapeGeometry";

const NumberField = ({ label, value, onChange, min, max, step = 1 }) => (
  <div>
    <Label className="text-xs mb-1 block">{label}</Label>
    <Input type="number" value={value} min={min} max={max} step={step} onChange={(event) => onChange(Number(event.target.value))} className="h-8" />
  </div>
);

const ColorField = ({ label, value, onChange }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="flex gap-2">
      <Input type="color" value={value} onChange={(event) => onChange(event.target.value)} className="h-8 w-16" />
      <Input value={value} onChange={(event) => onChange(event.target.value)} className="h-8 flex-1" />
    </div>
  </div>
);

export default function ShapeSettings({ layer, custom = {}, onChange, includeTransform = false, translate = (text) => text }) {
  const value = (key, fallback) => custom[key] ?? layer[key] ?? fallback;
  const set = (key) => (next) => onChange(key, next);

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Label className="text-xs font-medium">{translate("Shape")}</Label>
        <Select value={value("shapeType", "square")} onValueChange={set("shapeType")}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="square">{translate("Square / Rectangle")}</SelectItem><SelectItem value="circle">{translate("Circle / Oval")}</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="space-y-3 rounded-lg border border-slate-200 p-3">
        <Label className="text-xs font-medium">{translate("Shape Styling")}</Label>
        <ColorField label={translate("Background Color")} value={value("backgroundColor", "#14b8a6")} onChange={set("backgroundColor")} />
        <div>
          <div className="flex justify-between"><Label className="text-xs">{translate("Background Opacity")}</Label><span className="text-xs">{Math.round(value("backgroundOpacity", 1) * 100)}%</span></div>
          <Slider value={[value("backgroundOpacity", 1)]} min={0} max={1} step={0.01} onValueChange={([next]) => set("backgroundOpacity")(next)} />
        </div>
        <ColorField label={translate("Border Color")} value={value("borderColor", "#000000")} onChange={set("borderColor")} />
        <NumberField label={translate("Border Width (px)")} value={value("borderWidth", 0)} min={0} max={100} onChange={set("borderWidth")} />
      </div>

      {includeTransform && <div className="grid grid-cols-2 gap-3">
        <NumberField label={translate("X Position (%)")} value={value("position_x", 0)} onChange={set("position_x")} />
        <NumberField label={translate("Y Position (%)")} value={value("position_y", 0)} onChange={set("position_y")} />
        <NumberField label={translate("Width (%)")} value={value("width", 100)} min={0} onChange={set("width")} />
        <NumberField label={translate("Height (%)")} value={value("height", 100)} min={0} onChange={set("height")} />
        <NumberField label={translate("Scale")} value={value("scale", 1)} min={0.1} max={3} step={0.01} onChange={set("scale")} />
        <NumberField label={translate("Rotation (°)")} value={value("rotation", 0)} step={1} onChange={set("rotation")} />
        <div className="col-span-2">
          <div className="flex justify-between"><Label className="text-xs">{translate("Layer Opacity")}</Label><span className="text-xs">{Math.round(value("opacity", 1) * 100)}%</span></div>
          <Slider value={[value("opacity", 1)]} min={0} max={1} step={0.01} onValueChange={([next]) => set("opacity")(next)} />
        </div>
      </div>}

      {value("shapeType", "square") === "square" && <>
        <div className="space-y-3 rounded-lg border border-slate-200 p-3">
          <Label className="text-xs font-medium">{translate("Corner Position Offsets (%)")}</Label>
          <p className="text-xs text-slate-500">{translate("Offsets are relative to the shape's width and height. Positive Y moves a corner down.")}</p>
          {SHAPE_CORNERS.map((corner) => <div key={corner.x}>
            <Label className="text-xs text-slate-600">{translate(corner.label)}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <NumberField label="X" value={value(corner.x, 0)} onChange={set(corner.x)} />
              <NumberField label="Y" value={value(corner.y, 0)} onChange={set(corner.y)} />
            </div>
          </div>)}
        </div>
        <div className="space-y-3 rounded-lg border border-slate-200 p-3">
          <Label className="text-xs font-medium">{translate("Shape Corner Radius (px)")}</Label>
          <NumberField label={translate("All corners")} value={value("borderRadius", 0)} min={0} max={500} onChange={set("borderRadius")} />
          <div className="grid grid-cols-2 gap-3">
            {SHAPE_CORNERS.map((corner) => <NumberField key={corner.radius} label={translate(corner.label)} value={value(corner.radius, value("borderRadius", 0))} min={0} max={500} onChange={set(corner.radius)} />)}
          </div>
        </div>
      </>}
    </div>
  );
}
